// frontend/src/components/billing/billing-history.tsx
// Read DESIGN.md before modifying this file.
// Phase 2 (spec 2026-06-18): native org-scoped invoice history. Reads
// GET /organizations/:orgId/billing/invoices via Query (+ ?starting_after= for
// Load More) and renders an inline Retry on failure — never the global error
// page.
import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useInvoicesQuery, useLoadMoreInvoices, type Invoice } from './queries';

function fmtDate(created: number): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(created * 1000));
}

function fmtAmount(amountCents: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amountCents / 100);
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function fmtIsoDate(iso: string): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(iso));
}

export function BillingHistory({
  orgId,
  nextPaymentDate,
}: {
  orgId: string;
  nextPaymentDate?: string | null;
}) {
  const invoicesQuery = useInvoicesQuery(orgId);
  const loadMore = useLoadMoreInvoices(orgId);
  // Pages fetched via Load More, pinned to the base query's dataUpdatedAt so
  // any base refetch (Retry, invalidation) atomically drops stale extras —
  // no effect, no one-frame duplicate rows.
  const [more, setMore] = useState<{
    at: number;
    items: Invoice[];
    cursor: string | null;
  } | null>(null);
  const [moreError, setMoreError] = useState(false);

  const extras = more && more.at === invoicesQuery.dataUpdatedAt ? more : null;
  const invoices = [
    ...(invoicesQuery.data?.items ?? []),
    ...(extras?.items ?? []),
  ];
  const cursor = extras ? extras.cursor : invoicesQuery.data?.nextCursor ?? null;
  const loading = invoicesQuery.isPending || invoicesQuery.isFetching;
  const loadingMore = loadMore.isPending;
  const error = invoicesQuery.isError || moreError;

  function handleLoadMore(startingAfter: string) {
    setMoreError(false);
    loadMore.mutate(
      { startingAfter },
      {
        onSuccess: (page) =>
          setMore({
            at: invoicesQuery.dataUpdatedAt,
            items: [...(extras?.items ?? []), ...page.items],
            cursor: page.nextCursor,
          }),
        onError: () => setMoreError(true),
      },
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-lg font-semibold tracking-tight">Billing History</h3>
        {nextPaymentDate && (
          <span className="shrink-0 text-xs text-muted-foreground">
            Next payment {fmtIsoDate(nextPaymentDate)}
          </span>
        )}
      </div>
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          Loading invoices...
        </div>
      ) : error ? (
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            Unable to load invoices.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setMoreError(false);
              void invoicesQuery.refetch();
            }}
          >
            Retry
          </Button>
        </div>
      ) : invoices.length === 0 ? (
        <p className="text-sm text-muted-foreground">No invoices yet.</p>
      ) : (
        <>
          <div className="overflow-x-auto rounded-2xl border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice Number</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">
                    <span className="sr-only">View</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map((inv) => (
                  <TableRow key={inv.id}>
                    <TableCell className="font-medium">
                      {inv.number ?? '-'}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {fmtDate(inv.created)}
                    </TableCell>
                    <TableCell className="tabular-nums">
                      {fmtAmount(inv.amountCents, inv.currency)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {inv.status ? capitalize(inv.status) : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      {inv.hostedInvoiceUrl && (
                        <a
                          href={inv.hostedInvoiceUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                        >
                          View <span aria-hidden>→</span>
                        </a>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {cursor && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleLoadMore(cursor)}
              disabled={loadingMore}
            >
              {loadingMore && <Loader2 className="size-3.5 animate-spin" />}
              Load More
            </Button>
          )}
        </>
      )}
    </div>
  );
}
